import { Component, ViewChild, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { GetEducatorSearchQuery, WorkforcePersonMatchDto } from 'src/shared/services/api.service';
import { LeaStaffSearchComponent, LeaCreatePersonParams } from '../lea-staff-search/lea-staff-search.component';
import { AuthService } from 'src/auth/services/auth.service';
import { LeaCodeService } from 'src/auth/services/lea-code.service';
import { CertPolicies } from 'src/auth/cert-policies';
import { FeatureManagementService } from 'src/shared/services/feature-management.service';

interface CreatePersonSeed {
  searchedEin: string;
  dateOfBirth: Date | undefined;
  identity: { firstName: string; lastName: string; ssn: string; middleName?: string };
}

@Component({
  selector: 'cert-lea-staff-modal',
  templateUrl: './lea-staff-modal.component.html',
  styles: ``,
  standalone: false,
})
export class LeaStaffModalComponent {
  @ViewChild(LeaStaffSearchComponent) leaStaffSearchComponent!: LeaStaffSearchComponent;

  showSearch = signal(true);
  showCreateButton = signal(false);
  canLeaCreatePersons: boolean = this.authService.hasPolicy(CertPolicies.CanLeaCreatePersons);
  readonly lea2WorkforceManagementEnabled = toSignal(
    inject(FeatureManagementService).isFeatureEnabled('lea2WorkforceManagement'),
    { initialValue: false },
  );
  readonly leaCodeId = toSignal(inject(LeaCodeService).getCurrentLeaCodeId(), { initialValue: null });
  createPersonSeed = signal<CreatePersonSeed | undefined>(undefined);
  getSearchQuery = signal<GetEducatorSearchQuery>({ pageNumber: 1, pageSize: 10 });

  constructor(
    private readonly activeModal: NgbActiveModal,
    private readonly authService: AuthService,
  ) {}

  toggleShowSearch(query: GetEducatorSearchQuery | null) {
    this.showSearch.set(!this.showSearch());
    if (query) {
      this.getSearchQuery.set(query);
    }
  }

  onSearchExecuted(params: LeaCreatePersonParams) {
    const query = params.searchQuery;
    this.showCreateButton.set(true);

    this.createPersonSeed.set({
      searchedEin: query.ein ?? '',
      dateOfBirth: params.date,
      identity: {
        firstName: query.firstName ?? '',
        lastName: query.lastName ?? '',
        ssn: query.ssn ?? '',
        middleName: query.middleName,
      },
    });
  }

  // Return to the search results for the created or selected person's EIN.
  onPersonReady(result: { person: WorkforcePersonMatchDto; created: boolean }) {
    this.toggleShowSearch({ ein: result.person.ein, pageNumber: 1, pageSize: 10 });
  }

  close() {
    this.activeModal.close();
  }

  clearSearchForm() {
    this.leaStaffSearchComponent.clearFormInputs();
  }
}
